<template>
  <div class="editor-wrapper">

    <p class="editor-header">
      {{ title }}
    </p>

    <!-- TOOLBAR -->
    <div v-if="editor" v-show="isFocused && !isReadOnly" class="editor-toolbar">
      <!-- FONT DROPDOWN -->
      <div class="font-dropdown" @click.stop>
        <button
          type="button"
          class="font-button"
          @click="toggleFontDropdown"
        >
          {{ currentFontLabel }}
          <ad_form_icons
            :icon="'arrows'"
            class="button--icon"
            />
        </button>

        <div v-if="isFontDropdownOpen" class="font-menu">
          <div
            v-for="font in fonts"
            :key="font.value"
            class="font-option"
            :style="{ fontFamily: font.value || 'inherit' }"
            @click="selectFont(font.value)"
          >
            {{ font.label }}
          </div>
        </div>
      </div>

      <!-- TEXT TYPE DROPDOWN -->
      <div class="font-dropdown" @click.stop>
        <button
          type="button"
          class="type-button"
          @click="toggleTypeDropdown"
        >
          {{ currentTypeLabel }}
          <ad_form_icons
            :icon="'arrows'"
            class="button--icon"
            />
        </button>

        <div v-if="isTypeDropdownOpen" class="font-menu">
          <div
            v-for="type in textTypes"
            :key="type.label"
            class="font-option"
            @click="selectTextType(type)"
          >
            {{ type.label }}
          </div>
        </div>
      </div>

      <button
        :class="{ active: editor.isActive('bold') }"
        @mousedown.prevent="toggleBold"
      >
        B
      </button>

      <button
        :class="{ active: editor.isActive('italic') }"
        @mousedown.prevent="toggleItalic"
      >
        I
      </button>

      <button
        :class="{ active: editor.isActive('underline') }"
        @mousedown.prevent="toggleUnderline"
      >
        U
      </button>

      <button
        :class="{ active: editor.isActive('strike') }"
        @mousedown.prevent="toggleStrike"
      >
        <v-icon class="item-container--icon" >
          mdi-format-strikethrough-variant
        </v-icon>
      </button>

      <button
        :class="{ active: editor.isActive('bulletList') }"
        @mousedown.prevent="toggleBulletList"
      >
        <v-icon class="item-container--icon" >
          mdi-format-list-bulleted
        </v-icon>
      </button>

      <button
        :class="{ active: editor.isActive('orderedList') }"
        @mousedown.prevent="toggleOrderedList"
      >
        <v-icon class="item-container--icon" >
          mdi-format-list-numbered
        </v-icon>
      </button>

      <!-- TEXT COLOR PICKER -->
      <div class="color-picker-wrapper" @click.stop>
        <button
          type="button"
          title="Text color"
          class="color-picker-button"
          @mousedown.prevent="toggleTextColorPicker"
        >
          <ad_form_icons
            :icon="'aChar'"
            />
        </button>

        <div v-if="isTextColorPickerOpen" class="color-menu">
          <p class="color-menu-label">Text Color</p>
          <div class="color-swatches">
            <span
              v-for="color in colorSwatches"
              :key="color"
              class="color-swatch"
              :style="{ backgroundColor: color }"
              :class="{ 'color-swatch--active': currentTextColor === color }"
              @mousedown.prevent="setTextColor(color)"
            />
          </div>
          <div class="color-custom">
            <label class="color-custom-label">Custom</label>
            <input
              type="color"
              class="color-input"
              :value="currentTextColor"
              @mousedown.prevent
              @input="setTextColor($event.target.value)"
            />
          </div>
          <button class="color-clear-btn" @mousedown.prevent="clearTextColor">Clear</button>
        </div>
      </div>

      <!-- TEXT BG COLOR PICKER -->
      <div class="color-picker-wrapper" @click.stop>
        <button
          type="button"
          class="color-picker-button"
          title="Text highlight color"
          @click="toggleBgColorPicker"
        >
          <ad_form_icons
            :icon="'pencil'"
            />
        </button>

        <div v-if="isBgColorPickerOpen" class="color-menu">
          <p class="color-menu-label">Highlight Color</p>
          <div class="color-swatches">
            <span
              v-for="color in highlightSwatches"
              :key="color"
              class="color-swatch"
              :style="{ backgroundColor: color }"
              :class="{ 'color-swatch--active': currentBgColor === color }"
              @click="setBgColor(color)"
            />
          </div>
          <div class="color-custom">
            <label class="color-custom-label">Custom</label>
            <input
              type="color"
              class="color-input"
              :value="currentBgColor"
              @input="setBgColor($event.target.value)"
            />
          </div>
          <button class="color-clear-btn" @click="clearBgColor">Clear</button>
        </div>
      </div>

    </div>

    <!-- EDITOR -->
    <editor-content
      class="editor-content"
      :editor="editor"
    />

  </div>
</template>

<script>
import ad_form_icons from '@/assets/icons/ad_form_icons.vue';
import { Editor, EditorContent } from "@tiptap/vue-2"
import StarterKit from "@tiptap/starter-kit"
import Underline from "@tiptap/extension-underline"
import Placeholder from "@tiptap/extension-placeholder"
import TextStyle from "@tiptap/extension-text-style"
import FontFamily from '@tiptap/extension-font-family'
import Strike from "@tiptap/extension-strike"
import Color from "@tiptap/extension-color"
import Highlight from "@tiptap/extension-highlight"

export default {
  name: "RichTextEditor",

  components: {
    EditorContent,
    ad_form_icons
  },

  props: {
    value: {
      type: String,
      default: "",
    },
    title: {
      type: String,
      default: "",
    },
    isReadOnly: {
      type: Boolean,
      default: false,
    }
  },

  data() {
    return {
      editor: null,
      isFocused: false,
      isFontDropdownOpen: false,
      isTypeDropdownOpen: false,
      isTextColorPickerOpen: false,
      isBgColorPickerOpen: false,
      fonts: [
        { label: "Default", value: "" },
        { label: "Arial", value: "Arial" },
        { label: "Georgia", value: "Georgia" },
        { label: "Times New Roman", value: "Times New Roman" },
        { label: "Courier New", value: "Courier New" },
        { label: "Verdana", value: "Verdana" },
      ],
      textTypes: [
        { label: "Normal", level: null },
        { label: "Heading 1", level: 1 },
        { label: "Heading 2", level: 2 },
        { label: "Heading 3", level: 3 },
      ],
      colorSwatches: [
        "#000000", "#434343", "#666666", "#999999", "#B7B7B7", "#CCCCCC", "#FFFFFF",
        "#FF0000", "#FF9900", "#FFFF00", "#00FF00", "#00FFFF", "#0000FF", "#9900FF",
        "#FF00FF", "#F44336", "#E91E63", "#9C27B0", "#673AB7", "#3F51B5", "#2196F3",
        "#03A9F4", "#00BCD4", "#009688", "#4CAF50", "#8BC34A", "#CDDC39", "#FFC107",
      ],
      highlightSwatches: [
        "#FFFF00", "#00FF00", "#00FFFF", "#FF00FF", "#FF9900",
        "#FFD966", "#93C47D", "#76A5AF", "#EA9999", "#B4A7D6",
        "#FFE599", "#B6D7A8", "#A2C4C9", "#F4CCCC", "#D9D2E9",
      ],
    }
  },

  mounted() {
    this.editor = new Editor({
      editable: !this.isReadOnly,
      extensions: [
        StarterKit,
        Underline,
        TextStyle,
        FontFamily,
        Strike,
        // Color requires TextStyle to be registered first
        Color,
        // Highlight with multicolor support for bg color
        Highlight.configure({ multicolor: true }),
        Placeholder.configure({
          placeholder: "Start typing something amazing...",
        }),
      ],
      content: this.value,

      onUpdate: ({ editor }) => {
        this.$emit("input", editor.getHTML())
      },

      onFocus: () => {
        if (!this.isReadOnly) this.isFocused = true
      },
    })

    document.addEventListener("click", this.handleClickOutside)
  },

  beforeDestroy() {
    if (this.editor) {
      this.editor.destroy()
    }
    document.removeEventListener("click", this.handleClickOutside)
  },

  watch: {
    value(newValue) {
      if (this.editor && newValue !== this.editor.getHTML()) {
        this.editor.commands.setContent(newValue, false)
      }
    },
    isReadOnly(newValue) {
      if (this.editor) {
        this.editor.setEditable(!newValue)
        if (newValue) this.isFocused = false
      }
    },
  },

  computed: {
    currentFontLabel() {
      if (!this.editor) return "Default"
      const font = this.editor.getAttributes('textStyle').fontFamily
      const match = this.fonts.find(f => f.value === font)
      return match ? match.label : "Default"
    },
    currentTypeLabel() {
      if (!this.editor) return "Normal"
      if (this.editor.isActive('heading', { level: 1 })) return "Heading 1"
      if (this.editor.isActive('heading', { level: 2 })) return "Heading 2"
      if (this.editor.isActive('heading', { level: 3 })) return "Heading 3"
      return "Normal"
    },
    currentTextColor() {
      if (!this.editor) return "#000000"
      return this.editor.getAttributes('textStyle').color || "#000000"
    },
    currentBgColor() {
      if (!this.editor) return "#FFFF00"
      return this.editor.getAttributes('highlight').color || "#FFFF00"
    },
  },

  methods: {
    toggleBold() {
      this.editor.chain().focus().toggleBold().run()
    },
    toggleItalic() {
      this.editor.chain().focus().toggleItalic().run()
    },
    toggleStrike() {
      this.editor.chain().focus().toggleStrike().run()
    },
    toggleUnderline() {
      this.editor.chain().focus().toggleUnderline().run()
    },
    toggleBulletList() {
      this.editor.chain().focus().toggleBulletList().run()
    },
    toggleOrderedList() {
      this.editor.chain().focus().toggleOrderedList().run()
    },
    toggleFontDropdown() {
      this.isFontDropdownOpen = !this.isFontDropdownOpen
    },
    selectFont(font) {
      this.isFontDropdownOpen = false
      if (!font) {
        this.editor.chain().focus().unsetFontFamily().run()
      } else {
        this.editor.chain().focus().setFontFamily(font).run()
      }
    },
    handleClickOutside(event) {
      if (!this.$el.contains(event.target)) {
        this.isFocused = false
        this.isFontDropdownOpen = false
        this.isTypeDropdownOpen = false
        this.isTextColorPickerOpen = false
        this.isBgColorPickerOpen = false
      }
    },
    toggleTypeDropdown() {
      this.isTypeDropdownOpen = !this.isTypeDropdownOpen
    },
    selectTextType(type) {
      this.isTypeDropdownOpen = false
      if (!type.level) {
        this.editor.chain().focus().setParagraph().run()
      } else {
        this.editor.chain().focus().toggleHeading({ level: type.level }).run()
      }
    },

    // --- Text Color ---
    toggleTextColorPicker() {
      this.isTextColorPickerOpen = !this.isTextColorPickerOpen
      this.isBgColorPickerOpen = false
    },
    setTextColor(color) {
      this.editor.chain().focus().setColor(color).run()
    },
    clearTextColor() {
      this.editor.chain().focus().unsetColor().run()
      this.isTextColorPickerOpen = false
    },

    // --- BG / Highlight Color ---
    toggleBgColorPicker() {
      this.isBgColorPickerOpen = !this.isBgColorPickerOpen
      this.isTextColorPickerOpen = false
    },
    setBgColor(color) {
      this.editor.chain().focus().setHighlight({ color }).run()
    },
    clearBgColor() {
      this.editor.chain().focus().unsetHighlight().run()
      this.isBgColorPickerOpen = false
    },
  },
}
</script>

<style scoped>
.editor-wrapper {
  background-color: #F2F2F2;
  border-radius: 4px;
  padding: 15px;
}

.editor-header {
  font-family: 'Roboto', sans-serif;
  font-weight: 400;
  font-size: 16px;
  color: #A5A5A5;
  margin: 0 0 15px 0;
}

.editor-toolbar {
  display: flex;
  align-items: center;
  min-height: 45px;
  width: 100%;
  background-color: #FFFFFF;
  border: 1px solid #D2D2D2;
  padding: 10px 8px;
  margin-bottom: 15px;
  gap: 15px;
  flex-wrap: wrap;
}

.editor-toolbar button, .color-picker-button {
  background: transparent;
  border: none;
  padding: 1px 4px;
  font-family: 'Roboto', sans-serif;
  font-weight: 400;
  font-size: 16px;
  color: #000000;
  cursor: pointer;
  border-radius: 4px;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 24px !important;
}

.editor-toolbar button:hover {
  background-color: rgba(61, 37, 20, .12);
}

.button--icon {
  margin-left: 16px;
}

.font-dropdown {
  position: relative;
}

.font-button {
  background: #f5f5f5;
  border: 1px solid #ddd;
  padding: 6px 12px;
  font-weight: 500;
  cursor: pointer;
  border-radius: 4px;
  display: flex;
  align-items: center;
  gap: 6px;
}

.font-menu {
  position: absolute;
  top: 100%;
  left: 0;
  background: white;
  border: 1px solid #ddd;
  border-radius: 4px;
  margin-top: 4px;
  min-width: 160px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.08);
  z-index: 100;
}

.font-option {
  padding: 8px 12px;
  cursor: pointer;
}

.font-option:hover {
  background: #f0f0f0;
}

.editor-toolbar button.active {
  background: #6c63ff;
  color: white;
  border-color: #6c63ff;
}

.editor-content {
  min-height: 100px;
  outline: none;
}

::v-deep(.ProseMirror) {
  padding: 0;
}

.editor-content p {
  margin: 0 0 8px 0;
}

.editor-content ul,
.editor-content ol {
  padding-left: 20px;
}

/* ---- Color Picker ---- */
.color-picker-wrapper {
  position: relative;
}

.color-indicator {
  display: block;
  width: 18px;
  height: 4px;
  border-radius: 2px;
  border: 1px solid rgba(0,0,0,0.15);
}

.color-menu {
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  background: #fff;
  border: 1px solid #ddd;
  border-radius: 6px;
  padding: 10px;
  box-shadow: 0 4px 16px rgba(0,0,0,0.12);
  z-index: 200;
  min-width: 210px;
}

.color-menu-label {
  font-family: 'Roboto', sans-serif;
  font-size: 12px;
  font-weight: 500;
  color: #666;
  margin: 0 0 8px 0;
}

.color-swatches {
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
  margin-bottom: 10px;
}

.color-swatch {
  width: 20px;
  height: 20px;
  border-radius: 3px;
  cursor: pointer;
  border: 1px solid rgba(0,0,0,0.15);
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}

.color-swatch:hover {
  transform: scale(1.2);
  box-shadow: 0 2px 6px rgba(0,0,0,0.2);
}

.color-swatch--active {
  outline: 2px solid #6c63ff;
  outline-offset: 1px;
}

.color-custom {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}

.color-custom-label {
  font-family: 'Roboto', sans-serif;
  font-size: 12px;
  color: #666;
}

.color-input {
  width: 32px;
  height: 24px;
  padding: 0;
  border: 1px solid #ddd;
  border-radius: 3px;
  cursor: pointer;
  background: none;
}

.color-clear-btn {
  width: 100%;
  padding: 4px 0;
  font-family: 'Roboto', sans-serif;
  font-size: 12px;
  color: #666;
  background: #f5f5f5;
  border: 1px solid #ddd;
  border-radius: 4px;
  cursor: pointer;
  transition: background 0.2s ease;
}

.color-clear-btn:hover {
  background: #e8e8e8;
}
</style>